import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Calendar, Mail, Phone, ArrowLeft, Sparkles, PartyPopper, Heart, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const SignRentals = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [rentalData, setRentalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchRentals = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/rentals`);
        if (response.ok) {
          const data = await response.json();
          setRentalData(data);
        }
      } catch (error) {
        console.error('Error fetching rentals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRentals();
  }, []);

  const handleInquiry = (item) => {
    const subject = encodeURIComponent(`Rental Inquiry: ${item.name}`);
    const body = encodeURIComponent(`Hi,\n\nI'm interested in renting the "${item.name}" for my event.\n\nPlease provide more information about availability and pricing.\n\nThank you!`);
    window.location.href = `mailto:${rentalData?.settings?.contactEmail || 'info@okinawasignstudio.com'}?subject=${subject}&body=${body}`;
  };

  const categories = [
    { id: 'all', label: 'All Signs', icon: Sparkles },
    { id: 'Wedding', label: 'Wedding', icon: Heart },
    { id: 'Party', label: 'Party', icon: PartyPopper },
    { id: 'Business', label: 'Business', icon: Building2 },
  ];

  const filteredItems = rentalData?.items?.filter(item => 
    selectedCategory === 'all' || item.category === selectedCategory
  ) || [];

  // Show loading state
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
      >
        <div className="text-center">
          <div 
            className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4"
            style={{ 
              borderColor: isDark ? '#ff00ff' : '#ff1493',
              borderTopColor: 'transparent'
            }}
          />
          <p style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Loading rentals...</p>
        </div>
      </div>
    );
  }

  // Show message if rentals are disabled
  if (!rentalData?.enabled) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
      >
        <div className="text-center max-w-md px-4">
          <Sparkles 
            className="w-16 h-16 mx-auto mb-4"
            style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
          />
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ color: isDark ? '#ffffff' : '#0f172a' }}
          >
            Sign Rentals Coming Soon!
          </h2>
          <p 
            className="mb-8"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            We're preparing our rental collection. Check back soon for beautiful signs available for your events!
          </p>
          <Link to="/">
            <Button
              className="px-6 py-3"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                  : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                color: '#ffffff'
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { items, settings } = rentalData;

  return (
    <div 
      className="min-h-screen transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 px-4 overflow-hidden"
        style={{
          background: isDark 
            ? 'radial-gradient(circle at 20% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), #000000'
            : 'linear-gradient(135deg, #ffffff 0%, #fce7f3 50%, #ffffff 100%)'
        }}
      >
        {/* Decorative elements */}
        <div 
          className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse"
          style={{
            backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(236, 72, 153, 0.15)',
          }}
        />
        <div 
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{ 
            animationDelay: '1s',
            backgroundColor: isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(59, 130, 246, 0.15)',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 mb-8 transition-all duration-300 hover:gap-3"
            style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>

          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-sm mb-8"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              borderColor: isDark ? '#ff00ff' : '#ff1493',
              borderWidth: '2px',
              boxShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.3)' : '0 0 20px rgba(255, 20, 147, 0.4)'
            }}
          >
            <Calendar className="w-4 h-4" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
            <span 
              className="text-sm font-semibold"
              style={{ 
                color: isDark ? '#00ffff' : '#ff00ff',
                textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.8)' : '0 0 10px rgba(255, 0, 255, 0.6)'
              }}
            >
              Event Rentals Available
            </span>
          </div>

          {/* Title */}
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
            style={{
              color: isDark ? '#ffffff' : '#0f172a',
              textShadow: isDark 
                ? '0 0 30px rgba(255, 0, 255, 0.5), 0 0 60px rgba(255, 0, 255, 0.3)'
                : '0 0 25px rgba(255, 20, 147, 0.4)'
            }}
          >
            {settings?.sectionTitle || 'Sign Rentals'}
          </h1>

          <p 
            className="text-lg md:text-xl max-w-2xl mx-auto mb-8"
            style={{ 
              color: isDark ? '#00ff88' : '#9333ea',
              textShadow: isDark ? '0 0 15px rgba(0, 255, 136, 0.6)' : '0 0 15px rgba(147, 51, 234, 0.4)',
              fontWeight: '600'
            }}
          >
            {settings?.sectionSubtitle || 'Rent our beautiful signs for your special events'}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section 
        className="py-8 px-4 border-b"
        style={{ 
          backgroundColor: isDark ? '#050505' : '#fefefe',
          borderColor: isDark ? '#222' : '#e5e7eb'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-300"
                  style={{
                    backgroundColor: isActive 
                      ? (isDark ? '#ff00ff' : '#ff1493')
                      : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                    color: isActive 
                      ? '#ffffff'
                      : (isDark ? '#a0a0a0' : '#64748b'),
                    border: `2px solid ${isActive ? (isDark ? '#ff00ff' : '#ff1493') : 'transparent'}`,
                    boxShadow: isActive 
                      ? (isDark ? '0 0 20px rgba(255, 0, 255, 0.4)' : '0 0 20px rgba(255, 20, 147, 0.3)')
                      : 'none'
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rental Items Grid */}
      <section 
        className="py-16 px-4"
        style={{
          backgroundColor: isDark ? '#050505' : '#fefefe',
          background: isDark 
            ? 'linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #050505 100%)'
            : 'linear-gradient(180deg, #fefefe 0%, #fdf4ff 50%, #fefefe 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          {filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles 
                className="w-12 h-12 mx-auto mb-4"
                style={{ color: isDark ? '#666' : '#9ca3af' }}
              />
              <p style={{ color: isDark ? '#666' : '#9ca3af' }}>
                No rental signs available in this category yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                    border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
                    boxShadow: isDark 
                      ? '0 10px 40px rgba(255, 0, 255, 0.2)'
                      : '0 10px 40px rgba(255, 20, 147, 0.15)'
                  }}
                >
                  {/* Item Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={item.image || 'https://via.placeholder.com/400x300?text=Sign+Rental'}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    {/* Availability Badge */}
                    <div 
                      className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: item.available 
                          ? 'rgba(34, 197, 94, 0.9)'
                          : 'rgba(239, 68, 68, 0.9)',
                        color: '#ffffff'
                      }}
                    >
                      {item.available ? 'Available' : 'Booked'}
                    </div>
                    {/* Category Badge */}
                    <div 
                      className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-semibold capitalize"
                      style={{
                        backgroundColor: isDark ? 'rgba(255, 0, 255, 0.9)' : 'rgba(255, 20, 147, 0.9)',
                        color: '#ffffff'
                      }}
                    >
                      {item.category}
                    </div>
                  </div>

                  {/* Item Details */}
                  <div className="p-6">
                    <h3 
                      className="text-xl font-bold mb-2"
                      style={{ 
                        color: isDark ? '#ffffff' : '#0f172a',
                        textShadow: isDark ? '0 0 10px rgba(255, 255, 255, 0.3)' : 'none'
                      }}
                    >
                      {item.name}
                    </h3>
                    <p 
                      className="mb-4 text-sm"
                      style={{ color: isDark ? '#888' : '#64748b' }}
                    >
                      {item.description}
                    </p>

                    {/* Pricing */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      {item.pricePerDay && (
                        <div 
                          className="px-3 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(14, 165, 233, 0.1)',
                            border: `1px solid ${isDark ? '#00ffff' : '#0ea5e9'}`
                          }}
                        >
                          <span className="text-xs block" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>Per Day</span>
                          <span className="font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>¥{String(item.pricePerDay).replace(/[$¥€]/g, '').trim()}</span>
                        </div>
                      )}
                      {item.pricePerWeekend && (
                        <div 
                          className="px-3 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(255, 255, 0, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            border: `1px solid ${isDark ? '#ffff00' : '#f59e0b'}`
                          }}
                        >
                          <span className="text-xs block" style={{ color: isDark ? '#ffff00' : '#f59e0b' }}>Weekend</span>
                          <span className="font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>¥{String(item.pricePerWeekend).replace(/[$¥€]/g, '').trim()}</span>
                        </div>
                      )}
                    </div>

                    {/* Inquiry Button */}
                    <Button
                      onClick={() => handleInquiry(item)}
                      disabled={!item.available}
                      className="w-full py-3 font-semibold transition-all duration-300 hover:scale-105"
                      style={{
                        background: item.available
                          ? (isDark 
                            ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                            : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)')
                          : '#666',
                        color: '#ffffff',
                        boxShadow: item.available
                          ? (isDark 
                            ? '0 5px 30px rgba(255, 0, 255, 0.4)'
                            : '0 5px 30px rgba(255, 20, 147, 0.3)')
                          : 'none'
                      }}
                    >
                      <Mail className="w-4 h-4 mr-2 inline" />
                      {item.available ? 'Inquire Now' : 'Currently Booked'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section 
        className="py-16 px-4"
        style={{
          backgroundColor: isDark ? '#0a0a0a' : '#f8fafc',
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="p-8 md:p-12 rounded-3xl"
            style={{
              backgroundColor: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
              border: `2px solid ${isDark ? '#00ffff' : '#0ea5e9'}`,
              boxShadow: isDark 
                ? '0 10px 40px rgba(0, 255, 255, 0.15)'
                : '0 10px 40px rgba(14, 165, 233, 0.1)'
            }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ 
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark ? '0 0 15px rgba(0, 255, 255, 0.6)' : '0 0 12px rgba(14, 165, 233, 0.5)'
              }}
            >
              Have Questions About Rentals?
            </h2>
            <p 
              className="text-lg mb-8 max-w-2xl mx-auto"
              style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
            >
              Contact us for availability, custom requests, delivery options, or bulk rentals for your event. We're here to make your occasion shine!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={`mailto:${settings?.contactEmail || 'info@okinawasignstudio.com'}`}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                    : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                  color: '#ffffff',
                  boxShadow: isDark 
                    ? '0 5px 30px rgba(255, 0, 255, 0.4)'
                    : '0 5px 30px rgba(255, 20, 147, 0.3)'
                }}
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
              {settings?.contactPhone && (
                <a 
                  href={`tel:${settings.contactPhone}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(14, 165, 233, 0.1)',
                    border: `2px solid ${isDark ? '#00ffff' : '#0ea5e9'}`,
                    color: isDark ? '#00ffff' : '#0ea5e9'
                  }}
                >
                  <Phone className="w-5 h-5" />
                  {settings.contactPhone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignRentals;
